import { IconButton, InputBase } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchGlass } from '../styles/Icons'
import '../styles/search.scss'

interface SearchProps {
  placeholder?: string
  searchValue?: string
  onSearchChange: (value: string) => void
  resetValue: () => void
  debounceTime?: number
}

const OPSearch = (props: SearchProps): JSX.Element => {
  const { placeholder, searchValue, onSearchChange, resetValue, debounceTime = 300 } = props
  const { t } = useTranslation()

  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>(searchValue || '')
  const [fillState, setFillState] = useState('#757575')
  const ref = useRef(null)
  const debounceTimerRef = useRef<number | null>(null)

  useEffect(() => {
    // Sync the component's internal value with external searchValue
    // Only if they differ and searchValue is defined
    if (searchValue !== undefined && searchValue !== inputValue) {
      handleInputChange(searchValue)
    }
  }, [])

  const handleInputChange = (value: string) => {
    const newValue = value
    setInputValue(newValue)
    setIsSearching(newValue.length > 0)

    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = window.setTimeout(() => {
      onSearchChange(newValue)
    }, debounceTime)
  }

  const handleReset = () => {
    setInputValue('')
    setIsSearching(false)
    resetValue()

    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }

  const setActive = () => setFillState('#3C3C3B')
  const setDefault = () => {
    if (document.activeElement !== ref.current && !isSearching) setFillState('#757575')
  }

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return (
    <InputBase
      inputRef={ref}
      onMouseEnter={setActive}
      onMouseLeave={setDefault}
      onFocus={setActive}
      onBlur={setDefault}
      onChange={(e) => handleInputChange(e.target.value)}
      endAdornment={
        <IconButton disableRipple sx={{ padding: '0' }}>
          <SearchGlass fill={fillState} />
        </IconButton>
      }
      className='searchfield filtering'
      placeholder={placeholder ? placeholder : t('Search.Find')}
      type='search'
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleReset()
        }
      }}
      value={inputValue}
    />
  )
}
export default observer(OPSearch)
